import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, X, Check, FileCheck2 } from "lucide-react";

export default function AdminVerifications() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [visas, setVisas] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/admin/verifications").then((r) => setItems(r.data.pending)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/visa-verifications").then((r) => setVisas(r.data.items)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role]);

  if (user && user.role !== "admin") {
    return <div className="max-w-3xl mx-auto p-10"><Card className="p-8 text-center">Admin only.</Card></div>;
  }

  const decide = async (id, decision) => {
    setBusy(id + decision);
    try { await api.post(`/admin/verifications/${id}`, { decision }); toast.success(`Marked ${decision}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const decideVisa = async (id, decision) => {
    setBusy(id + decision);
    try { await api.post(`/admin/visa-verifications/${id}`, { decision }); toast.success(`Visa ${decision}`); load(); }
    catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : formatApiError(e));
    }
    finally { setBusy(""); }
  };

  const pendingVisas = visas.filter((v) => v.visa_status === "pending");
  const otherVisas = visas.filter((v) => v.visa_status !== "pending");

  const VISA_BADGE = {
    verified: "bg-emerald-50 text-emerald-800 border-emerald-300",
    pending: "bg-amber-50 text-amber-800 border-amber-300",
    expired: "bg-red-50 text-red-800 border-red-300",
    unverified: "bg-slate-100 text-slate-700 border-slate-300",
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 space-y-12">
      <section>
        <div className="flex items-center gap-3 mb-8">
          <FileCheck2 className="w-8 h-8 text-[#6B1D2F]" />
          <div>
            <h1 className="font-serif text-3xl font-semibold text-slate-900">Visa Share-Code Checks</h1>
            <p className="text-slate-600">Clicking Verify runs a live government immigration verification check via the integrated provider — the badge is set only on an ACCEPTED outcome.</p>
          </div>
        </div>
        {pendingVisas.length === 0 && <Card className="p-8 text-center border-[#EAE3D2] bg-white text-slate-600">No codes awaiting verification.</Card>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pendingVisas.map((v) => (
            <Card key={v.id} className="p-5 border-[#EAE3D2] bg-white" data-testid={`admin-visa-${v.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-serif text-lg text-slate-900">{v.full_name}</h3>
                  <p className="text-xs text-slate-500">{v.email}</p>
                </div>
                <Badge className={VISA_BADGE[v.visa_status]}>Pending</Badge>
              </div>
              <div className="mt-3 text-sm text-slate-700 space-y-1">
                <p><span className="text-slate-500">Visa type:</span> {v.visa_type}</p>
                <p><span className="text-slate-500">Expiry:</span> {v.visa_expiry_date}</p>
                <p><span class="text-slate-500">Share code:</span> <code className="bg-[#FAF6F0] px-2 py-0.5 rounded font-mono">{v.visa_share_code}</code></p>
                {v.visa_check_outcome && <p className="text-xs text-slate-500">Last provider check: <b>{v.visa_check_outcome}</b>{v.visa_check_at ? ` · ${new Date(v.visa_check_at).toLocaleString()}` : ""}</p>}
              </div>
              <div className="flex gap-2 mt-4">
                <Button data-testid={`admin-visa-approve-${v.id}`} onClick={() => decideVisa(v.id, "verified")} disabled={!!busy} className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"><Check className="w-4 h-4 mr-1" />Verify</Button>
                <Button data-testid={`admin-visa-reject-${v.id}`} onClick={() => decideVisa(v.id, "rejected")} disabled={!!busy} variant="outline" className="flex-1 rounded-full"><X className="w-4 h-4 mr-1" />Reject</Button>
              </div>
            </Card>
          ))}
        </div>
        {otherVisas.length > 0 && (
          <Card className="mt-6 p-5 border-[#EAE3D2] bg-white">
            <h3 className="font-serif text-lg text-slate-900 mb-3">All other members</h3>
            <div className="divide-y divide-[#EAE3D2] text-sm">
              {otherVisas.map((v) => (
                <div key={v.id} className="py-2 flex items-center justify-between gap-3">
                  <span className="text-slate-800">{v.full_name} <span className="text-slate-500">· {v.visa_type} · exp {v.visa_expiry_date}</span></span>
                  <Badge className={VISA_BADGE[v.visa_status]}>{v.visa_status}</Badge>
                </div>
              ))}
            </div>
          </Card>
        )}
      </section>

      <section>
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="w-8 h-8 text-[#6B1D2F]" />
          <div>
            <h1 className="font-serif text-3xl font-semibold text-slate-900">Selfie Verification Review</h1>
            <p className="text-slate-600">Approve or reject pending selfie submissions.</p>
          </div>
        </div>
        {items.length === 0 && <Card className="p-10 text-center border-[#EAE3D2] bg-white text-slate-600">No pending verifications.</Card>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((u) => (
            <Card key={u.id} className="overflow-hidden border-[#EAE3D2] bg-white" data-testid={`admin-verif-${u.id}`}>
              <div className="aspect-square bg-[#F3EFE6]">
                {u.verification_selfie_path && <img src={fileUrl(u.verification_selfie_path)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="p-4">
                <h3 className="font-serif text-lg text-slate-900">{u.full_name}, {u.age}</h3>
                <p className="text-xs text-slate-500">{u.email}</p>
                <Badge className="mt-2 bg-amber-50 text-amber-800 border-amber-200">Pending</Badge>
                <div className="flex gap-2 mt-4">
                  <Button data-testid={`admin-approve-${u.id}`} onClick={() => decide(u.id, "verified")} disabled={!!busy} className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full"><Check className="w-4 h-4 mr-1" />Approve</Button>
                  <Button data-testid={`admin-reject-${u.id}`} onClick={() => decide(u.id, "rejected")} disabled={!!busy} variant="outline" className="flex-1 rounded-full"><X className="w-4 h-4 mr-1" />Reject</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
